import { ADVANCED_FILTER_SEPARATOR } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";

const ADVANCED_FILTERS_PARAM = "advancedFilters";

export interface ViewTabLink {
  key: string;
  href: string;
  /** Other paths that show this tab's table, such as the type's index. */
  aliases?: readonly string[];
}

function normalizePath(path: string): string {
  const decoded = decodeURIComponent(path);
  return decoded.length > 1 ? decoded.replace(/\/+$/, "") : decoded;
}

function parseHref(href: string): { path: string; filters: string | null } {
  const url = new URL(href, "http://record-view-tab.invalid");
  return {
    path: normalizePath(url.pathname),
    filters: url.searchParams.get(ADVANCED_FILTERS_PARAM),
  };
}

function splitFilters(param: string | null): string[] {
  return param ? param.split(ADVANCED_FILTER_SEPARATOR).filter(Boolean) : [];
}

/** The tab whose filters the location shows exactly, else still carries (a built-in tab with a filter added), else the table's unfiltered tab. */
export function selectedViewTab<T extends ViewTabLink>(
  tabs: readonly T[],
  pathname: string,
  search: string,
): T | undefined {
  const path = normalizePath(pathname);
  const filters = new URLSearchParams(search).get(ADVANCED_FILTERS_PARAM);
  const applied = splitFilters(filters);
  const onThisTable = tabs.flatMap((tab) => {
    const link = parseHref(tab.href);
    const paths = [link.path, ...(tab.aliases ?? []).map(normalizePath)];
    return paths.includes(path) ? [{ tab, filters: link.filters }] : [];
  });
  return (
    onThisTable.find((candidate) => candidate.filters === filters)?.tab ??
    onThisTable.find((candidate) => {
      const own = splitFilters(candidate.filters);
      return own.length > 0 && own.every((filter) => applied.includes(filter));
    })?.tab ??
    onThisTable.find((candidate) => candidate.filters === null)?.tab
  );
}

/**
 * The view name when the location is a view of the All tab's table that has
 * no tab of its own, such as a bookmarked system view the tabs hide.
 */
export function getHiddenViewName(
  allHref: string,
  pathname: string,
  isHiddenView: (viewName: string) => boolean,
): string | undefined {
  const tablePath = parseHref(allHref).path.replace(/\/[^/]*$/, "");
  const path = normalizePath(pathname);
  if (!path.startsWith(`${tablePath}/`)) {
    return undefined;
  }
  const viewName = path.slice(tablePath.length + 1);
  return !viewName.includes("/") && isHiddenView(viewName)
    ? viewName
    : undefined;
}
